import {
  ActionIcon,
  Button,
  Card,
  Container,
  Flex,
  Group,
  Modal,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconArrowsMaximize, IconFileText, IconSignature } from '@tabler/icons';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { CustomLoader } from '../../components/custom-loader';
import { rootNavigate } from '../../components/custom-router';
import { Page } from '../../components/page';
import {
  useFindEmployeePayslipByHashCode,
  useSendVerificationCode,
  useSignatureEmployeePaySlip,
  useVerifyCpf,
  useVerifyCode,
} from '../../data/hooks/employee-payslip';
import { useUploadMultipleFiles } from '../../data/hooks/files';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';

type PageModalState =
  | 'verify-code'
  | 'signature'
  | 'success-signature'
  | 'preview'
  | null;

export function EmployeePaySlip() {
  const { hashcode } = useParams();
  const [pageModal, setPageModal] = useState<PageModalState>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [cpf, setCpf] = useState('');
  const [cpfVerified, setCpfVerified] = useState(false);
  const sigCanvas = useRef<any>(null);

  const {
    fetch: findByHashCodeFetcher,
    loading: findByHashCodeLoader,
    response: findByHashCodeResponse,
  } = useFindEmployeePayslipByHashCode();

  const { fetch: verifyCpfFetcher, loading: verifyCpfLoader } = useVerifyCpf();

  const { fetch: uploadFilesFetcher, loading: uploadFilesLoader } =
    useUploadMultipleFiles();

  const { fetch: signatureDocumentFetcher, loading: signatureDocumentLoader } =
    useSignatureEmployeePaySlip();

  const { fetch: sendCodeFetcher, loading: sendCodeLoader } =
    useSendVerificationCode();

  const { fetch: verifyCodeFetcher, loading: verifyCodeLoader } =
    useVerifyCode();

  async function handleVerifyCpf() {
    if (!hashcode) return;
    if (!cpf.replace(/\D/g, '')) return;
    await verifyCpfFetcher({
      hashCode: hashcode,
      cpf,
      onSuccess: () => {
        setCpfVerified(true);
        findByHashCodeFetcher({
          hashCode: hashcode,
          onError: () => {
            errorNotification({
              title: 'Link inválido ou expirado',
              message: 'Este link de holerite não está mais disponível.',
            });
          },
        });
      },
    });
  }

  function handleCloseModal() {
    setPageModal(null);
    setVerificationCode('');
  }

  async function handleRequestCode() {
    if (!hashcode) return;
    await sendCodeFetcher({
      hashCode: hashcode,
      onSuccess: () => {
        successNotification({
          title: 'Código enviado!',
          message: 'Verifique seu e-mail e insira o código de 6 dígitos.',
        });
        setPageModal('verify-code');
      },
    });
  }

  async function handleCodeConfirmed() {
    if (!hashcode) return;
    if (verificationCode.length < 6) {
      errorNotification({
        title: 'Código incompleto',
        message: 'Digite os 6 dígitos do código enviado por e-mail.',
      });
      return;
    }
    await verifyCodeFetcher({
      hashCode: hashcode,
      verificationCode,
      onSuccess: () => setPageModal('signature'),
    });
  }

  const clearSignature = () => sigCanvas.current?.clear();

  const dataURLtoFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(',');
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  async function handleUploadFiles(
    fileList: File[],
    errorMessage: string,
  ): Promise<number[]> {
    let fileIds: number[] = [];

    if (!fileList.length) return [];

    try {
      await uploadFilesFetcher({
        data: { files: fileList },
        onSuccess: (res) => {
          fileIds = res;
        },
      });
      return fileIds;
    } catch (error) {
      errorNotification({
        title: 'Erro ao realizar upload da assinatura',
        message: errorMessage,
      });
      return [];
    }
  }

  async function handleSubmitSignature() {
    if (!hashcode) return;
    if (sigCanvas.current?.isEmpty()) {
      errorNotification({
        title: 'Assinatura em branco',
        message: 'Por favor, desenhe sua assinatura antes de salvar.',
      });
      return;
    }
    const dataURL = sigCanvas.current.toDataURL('image/png');
    const sigCanvasImage = dataURLtoFile(dataURL, 'assinatura.png');

    const fileIds = await handleUploadFiles(
      [sigCanvasImage],
      'Tente novamente.',
    );

    await signatureDocumentFetcher({
      data: {
        hashCode: hashcode,
        signatureFile: fileIds[0],
        verificationCode,
      },
      onSuccess: () => {
        handleCloseModal();
        successNotification({
          title: 'Assinatura enviada com sucesso!',
          message: 'Tudo certo',
        });
        setPageModal('success-signature');
      },
    });
  }

  if (!cpfVerified) {
    return (
      <Page>
        <CustomLoader loading={verifyCpfLoader} />
        <Container size="xs">
          <Flex direction="column" align="center" gap="lg" mt={80} ta="center">
            <IconFileText size={50} color="#228be6" />
            <Title order={2}>Verificação de identidade</Title>
            <Text c="dimmed" maw={360}>
              Para acessar seu holerite, informe seu CPF cadastrado.
            </Text>
            <Text size="sm" c="dimmed" maw={360}>
              Digite os 11 números do seu CPF, com ou sem pontos e traço (ex.:
              123.456.789-00 ou 12345678900). Não digite letras nem espaços.
            </Text>
            <TextInput
              placeholder="000.000.000-00"
              size="lg"
              w="100%"
              inputMode="numeric"
              maxLength={14}
              value={cpf}
              onChange={(e) =>
                setCpf(e.currentTarget.value.replace(/[^\d.-]/g, ''))
              }
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyCpf()}
            />
            <Button
              size="lg"
              radius="xl"
              fullWidth
              onClick={handleVerifyCpf}
              loading={verifyCpfLoader}
              variant="gradient"
              gradient={{ from: 'teal', to: 'cyan', deg: 45 }}
            >
              Confirmar
            </Button>
          </Flex>
        </Container>
      </Page>
    );
  }

  return (
    <Page>
      <CustomLoader loading={findByHashCodeLoader || uploadFilesLoader} />
      <Container size="xl">
        <Flex direction="column" align="center" mb="xl" ta="center">
          <IconFileText size={50} color="#228be6" />
          <Title order={2} mt="md">
            Visualização do Holerite
          </Title>
          <Text c="dimmed" mt={4} maw={420}>
            Confira atentamente as informações do seu holerite antes de assinar.
          </Text>
        </Flex>
        <Card
          shadow="md"
          radius="md"
          p="sm"
          withBorder
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflowX: 'auto',
            overflowY: 'hidden',
            backgroundColor: '#f9fafb',
          }}
        >
          {findByHashCodeResponse?.url ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <Tooltip label="Expandir visualização" position="top">
                <ActionIcon
                  variant="filled"
                  color="blue"
                  size="lg"
                  style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}
                  onClick={() => setPageModal('preview')}
                >
                  <IconArrowsMaximize size={18} />
                </ActionIcon>
              </Tooltip>
              <iframe
                src={
                  findByHashCodeResponse.signedDocumentUrl ??
                  findByHashCodeResponse.url
                }
                style={{
                  width: '100%',
                  border: 'none',
                  height: '360px',
                  borderRadius: '8px',
                  background: '#fff',
                }}
              />
            </div>
          ) : (
            <Flex align="center" justify="center" h={300}>
              <Text c="dimmed">Nenhum documento disponível.</Text>
            </Flex>
          )}
        </Card>

        <Group position="center" mt="xl">
          {findByHashCodeResponse?.signedAt ? (
            <Flex direction="column" align="center" gap="sm">
              <Text c="green" fw="bold" size="lg">
                Holerite já assinado em{' '}
                {new Date(findByHashCodeResponse.signedAt).toLocaleDateString(
                  'pt-BR',
                )}
                .
              </Text>
              {findByHashCodeResponse.signedBy && (
                <Text c="dimmed" size="sm">
                  Assinado por: {findByHashCodeResponse.signedBy}
                </Text>
              )}
              {findByHashCodeResponse.signedDocumentUrl && (
                <Button
                  component="a"
                  href={findByHashCodeResponse.signedDocumentUrl}
                  target="_blank"
                  variant="outline"
                  color="teal"
                  size="sm"
                  radius="xl"
                >
                  Baixar documento assinado
                </Button>
              )}
            </Flex>
          ) : (
            <Button
              size="lg"
              radius="xl"
              leftIcon={<IconSignature size={20} />}
              disabled={!findByHashCodeResponse?.url}
              onClick={handleRequestCode}
              loading={sendCodeLoader}
              variant="gradient"
              gradient={{ from: 'teal', to: 'cyan', deg: 45 }}
            >
              Assinar Holerite
            </Button>
          )}
        </Group>
      </Container>

      <Modal
        opened={pageModal === 'preview'}
        onClose={handleCloseModal}
        size="90%"
        title="Visualização do Holerite"
      >
        <iframe
          src={
            findByHashCodeResponse?.signedDocumentUrl ??
            findByHashCodeResponse?.url
          }
          style={{
            width: '100%',
            border: 'none',
            height: '80vh',
            borderRadius: '8px',
            background: '#fff',
          }}
        />
      </Modal>

      <Modal
        opened={pageModal === 'verify-code'}
        onClose={handleCloseModal}
        closeOnClickOutside={false}
        closeOnEscape={true}
        title="Verificação de identidade"
      >
        <Flex direction="column" align="center" gap="lg" py="md">
          <Text size="md" ta="center" c="dimmed">
            Enviamos um código de 6 dígitos para o seu e-mail. Insira o código
            abaixo para continuar.
          </Text>
          <TextInput
            placeholder="000000"
            size="xl"
            maxLength={6}
            value={verificationCode}
            onChange={(e) =>
              setVerificationCode(e.currentTarget.value.replace(/\D/g, ''))
            }
            styles={{
              input: {
                textAlign: 'center',
                letterSpacing: '0.5em',
                fontSize: '1.5rem',
              },
            }}
          />
          <Flex gap="md">
            <Button
              variant="subtle"
              color="gray"
              onClick={handleRequestCode}
              loading={sendCodeLoader}
            >
              Reenviar código
            </Button>
            <Button
              radius="xl"
              onClick={handleCodeConfirmed}
              loading={verifyCodeLoader}
              variant="gradient"
              gradient={{ from: 'teal', to: 'cyan', deg: 45 }}
              size="md"
            >
              Confirmar
            </Button>
          </Flex>
        </Flex>
      </Modal>

      <Modal
        opened={pageModal === 'signature'}
        onClose={handleCloseModal}
        closeOnClickOutside={false}
        closeOnEscape={true}
        size={'xl'}
      >
        <Flex direction={'column'} align={'center'}>
          <Text size="lg" mb="sm" ta="center">
            Use o mouse ou o dedo para desenhar sua assinatura no campo abaixo.
          </Text>
          <Card
            shadow="md"
            radius="md"
            p="sm"
            withBorder
            mb={'lg'}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflowX: 'auto',
              overflowY: 'hidden',
              backgroundColor: '#f9fafb',
            }}
          >
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{
                width: 700,
                height: 300,
              }}
            />
          </Card>

          <Flex gap={'md'} align={'center'}>
            <Button
              radius="xl"
              disabled={!findByHashCodeResponse?.url}
              color="gray"
              onClick={clearSignature}
              size="lg"
            >
              Limpar
            </Button>
            <Button
              radius="xl"
              leftIcon={<IconSignature size={20} />}
              disabled={!findByHashCodeResponse?.url}
              onClick={() => handleSubmitSignature()}
              loading={signatureDocumentLoader}
              variant="gradient"
              gradient={{ from: 'teal', to: 'cyan', deg: 45 }}
              size="lg"
            >
              Salvar
            </Button>
          </Flex>
        </Flex>
      </Modal>

      <Modal
        title="Sucesso"
        opened={pageModal === 'success-signature'}
        onClose={() => setPageModal(null)}
        closeOnClickOutside={false}
        withCloseButton={false}
        closeOnEscape={false}
      >
        <Group>
          <Text fw="bold">Tudo certo!</Text>
          <Text>Assinatura enviada com sucesso!</Text>
        </Group>
        <Group position="right">
          <Button onClick={() => rootNavigate('/')}>OK</Button>
        </Group>
      </Modal>
    </Page>
  );
}
